import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crosshair } from "@/components/Crosshair";
import { Badge, BadgeRow } from "@/components/Badges";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/lib/toast";
import {
  ArrowLeft, AtSign, ShieldCheck, Award, Target, CheckCircle2, Circle,
  Loader2, Lock, Fingerprint, Save, FileCheck, Mail, Phone, MapPin, AlertTriangle, KeyRound, X, Pencil, Share2, Cake, LogOut, Bell,
} from "lucide-react";

const SPECIALTIES = ["Hunting", "Competition", "Long Range", "Duty/Patrol", "Collector"];
const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm transition-colors";

export function Section({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <div className="border border-sniper bg-surface p-5 mt-5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-olive-bright" />
        <h2 className="font-heading text-xl uppercase tracking-tight">{title}</h2>
      </div>
      {subtitle && <p className="text-xs font-body text-secondary-foreground mt-1">{subtitle}</p>}
      <Crosshair className="my-4" />
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- Username
export function UsernameEditor({ current, onChanged }) {
  const [draft, setDraft] = useState(current || "");
  const [check, setCheck] = useState(null); // {valid, available, reason}
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldownMsg, setCooldownMsg] = useState("");
  const timer = useRef(null);
  const changed = draft.trim() !== (current || "");

  useEffect(() => { setDraft(current || ""); }, [current]);

  useEffect(() => {
    setCooldownMsg("");
    if (!changed || !draft.trim()) { setCheck(null); return; }
    setChecking(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/username/check?u=${encodeURIComponent(draft.trim())}`);
        setCheck(data);
      } catch { setCheck(null); }
      finally { setChecking(false); }
    }, 400);
    return () => timer.current && clearTimeout(timer.current);
  }, [draft, changed]);

  const ready = changed && check?.valid && check?.available && !busy;

  const save = async () => {
    if (!ready) return;
    setBusy(true); setCooldownMsg("");
    try {
      const { data } = await api.post("/me/username", { username: draft.trim() });
      toast.success(`Username changed to @${data.username}`);
      onChanged();
    } catch (err) {
      const st = err.response?.status;
      const detail = formatError(err.response?.data?.detail);
      if (st === 429) setCooldownMsg(detail);
      toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-[11px] uppercase text-muted-foreground">Current</span>
        <span className="font-heading text-lg text-foreground" data-testid="settings-current-username">@{current}</span>
      </div>
      <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground block mb-1">New username</label>
      <div className="relative">
        <AtSign className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
          data-testid="settings-username-input"
          maxLength={20}
          className={`${inputCls} pl-9`}
          placeholder="your_handle"
          autoCapitalize="none"
          spellCheck={false}
        />
      </div>
      <div className="min-h-[20px] mt-1.5" data-testid="settings-username-status">
        {checking && <span className="font-mono text-[11px] text-muted-foreground inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> checking…</span>}
        {!checking && changed && check && check.valid && check.available && (
          <span className="font-mono text-[11px] text-olive-bright inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> @{check.username} is available</span>
        )}
        {!checking && changed && check && (!check.valid || !check.available) && (
          <span className="font-mono text-[11px] text-blaze">{check.reason}</span>
        )}
      </div>
      {cooldownMsg && <p className="font-mono text-[11px] text-amber-400 mt-1" data-testid="settings-username-cooldown">{cooldownMsg}</p>}
      <button onClick={save} disabled={!ready} data-testid="settings-username-save"
        className="mt-3 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold uppercase text-sm px-5 py-2.5 transition-colors">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Change username"}
      </button>
      <p className="font-mono text-[10px] uppercase text-muted-foreground mt-2 leading-relaxed">
        Your old handle stays reserved for 90 days and redirects here, so shared links never break.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- About you (real name + privacy + bio)
const BIO_MAX = 300;
export function AboutYouEditor({ handle = "", initialFirst = "", initialLast = "", initialShowName = false, initialBio = "", onChanged }) {
  const [first, setFirst] = useState(initialFirst || "");
  const [last, setLast] = useState(initialLast || "");
  const [showName, setShowName] = useState(!!initialShowName);
  const [bio, setBio] = useState(initialBio || "");
  const [busy, setBusy] = useState(false);
  const len = bio.length;
  const counterCls = len >= BIO_MAX ? "text-blaze" : len >= 260 ? "text-amber-400" : "text-muted-foreground";
  const hasName = !!(first.trim() || last.trim());
  const dirty = first.trim() !== (initialFirst || "") || last.trim() !== (initialLast || "")
    || showName !== !!initialShowName || bio !== (initialBio || "");
  const fullName = [first.trim(), last.trim()].filter(Boolean).join(" ");

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/me/profile", {
        first_name: first.trim(), last_name: last.trim(),
        show_name_public: hasName ? showName : false, bio,
      });
      toast.success("Profile updated.");
      onChanged?.(data.user);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="about-you-editor" className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground block mb-1">First name</label>
          <input value={first} onChange={(e) => setFirst(e.target.value)} maxLength={60} data-testid="about-first-name-input"
            placeholder="First"
            className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm" />
        </div>
        <div>
          <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground block mb-1">Last name</label>
          <input value={last} onChange={(e) => setLast(e.target.value)} maxLength={60} data-testid="about-last-name-input"
            placeholder="Last"
            className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm" />
        </div>
      </div>

      {/* Public-name privacy toggle — default OFF. Admins can always see your name for support. */}
      <div className="flex items-start justify-between gap-4 border border-sniper bg-input/40 px-3 py-3" data-testid="show-name-toggle-row">
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">Show my name on my public profile</div>
          <p className="text-[11px] text-muted-foreground mt-1 font-body leading-relaxed">
            {!hasName
              ? "Add your first or last name above to turn this on."
              : (showName
                  ? <>Buyers will see <span className="text-foreground">{fullName}</span> next to <span className="text-foreground">@{handle}</span>.</>
                  : <>Off — only your <span className="text-foreground">@{handle}</span> handle is public. Your real name stays private (our team can still see it for support &amp; safety).</>)}
          </p>
        </div>
        <Switch checked={hasName && showName} disabled={!hasName}
          onCheckedChange={(v) => setShowName(!!v)} data-testid="show-name-public-toggle"
          aria-label="Show my name on my public profile" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">About you</label>
          <span data-testid="about-bio-counter" className={`font-mono text-[11px] ${counterCls}`}>{len}/{BIO_MAX}</span>
        </div>
        <textarea value={bio} onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX))} rows={3} maxLength={BIO_MAX}
          data-testid="about-bio-input" placeholder="What you shoot, what you're usually hunting for, and how fast you ship. Buyers read this."
          className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm resize-none" />
        <p className="font-mono text-[10px] text-muted-foreground mt-1">Optional. Plain text — no links, and no contact details (deals arranged off OpticSwap aren't covered by buyer protection).</p>
      </div>
      <button onClick={save} disabled={busy || !dirty} data-testid="about-save-btn"
        className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-4 py-2 transition-colors">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}


// ---------------------------------------------------------------- Identity
export function IdentityPanel() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/identity/status").then(({ data }) => setSt(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/identity/session");
      if (data.url) window.location.assign(data.url);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  if (!st) return <p className="font-mono text-xs text-muted-foreground uppercase">Loading…</p>;

  if (st.verified) {
    return (
      <div className="flex items-center gap-3" data-testid="identity-verified">
        <Badge badge={{ id: "verified_id", value: st.verification_year ? { year: st.verification_year } : null }} accountTone="green" />
        <span className="font-mono text-[11px] uppercase text-olive-bright inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Your identity is verified</span>
      </div>
    );
  }

  return (
    <div data-testid="identity-panel">
      <p className="text-sm font-body text-secondary-foreground">
        Verify your identity with Stripe Identity to earn the <span className="text-foreground">Verified ID</span> badge. You'll
        photograph a government ID and take a quick selfie. We never store your document — only that you passed.
      </p>
      {st.locked ? (
        <div className="mt-3 border border-blaze/40 bg-blaze/5 p-3 flex items-center gap-2" data-testid="identity-locked">
          <Lock className="w-4 h-4 text-blaze shrink-0" />
          <span className="font-mono text-[11px] uppercase text-blaze">Verification locked after {st.max_attempts} attempts — contact support to unlock.</span>
        </div>
      ) : (
        <button onClick={start} disabled={busy} data-testid="identity-verify-btn"
          className="mt-3 inline-flex items-center gap-2 bg-steel hover:bg-steel-bright disabled:opacity-50 text-obsidian font-heading font-bold uppercase text-sm px-5 py-2.5 transition-colors">
          <Fingerprint className="w-4 h-4" /> {busy ? "Opening…" : "Verify my ID"}
        </button>
      )}
      <p className="font-mono text-[10px] uppercase text-muted-foreground mt-2">
        Attempt {st.attempts} of {st.max_attempts}{st.status && st.status !== "unverified" ? ` · ${st.status}` : ""}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- Badges progress
export function BadgesPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/badge-progress").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <p className="font-mono text-xs text-muted-foreground uppercase">Loading…</p>;
  return (
    <div data-testid="badges-panel">
      <div className="mb-4">
        <div className="font-mono text-[10px] uppercase text-muted-foreground mb-2">Earned</div>
        {data.earned.length ? <BadgeRow badges={data.earned} accountTone={data.account_tone} />
          : <p className="font-mono text-xs text-muted-foreground">No badges yet — the checklist below shows how to earn them.</p>}
      </div>
      <div className="space-y-2">
        {data.progress.map((p) => (
          <div key={p.id} className="flex items-start gap-2.5 border border-sniper/70 px-3 py-2.5" data-testid={`badge-progress-${p.id}`}>
            {p.earned
              ? <CheckCircle2 className="w-4 h-4 text-olive-bright shrink-0 mt-0.5" />
              : <Circle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <div className={`font-mono text-xs uppercase ${p.earned ? "text-olive-bright" : "text-foreground"}`}>{p.label}</div>
              <div className="font-body text-[12px] text-secondary-foreground">{p.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Specialty
export function SpecialtyPicker({ initial, onChanged }) {
  const [tags, setTags] = useState(initial || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTags(initial || []); }, [initial]);
  const toggle = (t) => {
    setTags((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : (cur.length >= 3 ? cur : [...cur, t]));
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/specialty", { tags });
      setTags(data.specialty_tags);
      toast.success("Specialties updated.");
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="specialty-picker">
      <p className="text-xs font-body text-secondary-foreground mb-3">Pick up to 3 — these show on your profile and let buyers filter by what you shoot. They are not verified badges.</p>
      <div className="flex flex-wrap gap-2">
        {SPECIALTIES.map((t) => {
          const on = tags.includes(t);
          const disabled = !on && tags.length >= 3;
          return (
            <button key={t} onClick={() => toggle(t)} disabled={disabled} data-testid={`specialty-toggle-${t}`}
              className={`font-body text-sm rounded-full border px-4 py-1.5 transition-colors ${on
                ? "border-olive bg-olive/20 text-olive-bright"
                : disabled ? "border-sniper/50 text-muted-foreground/40 cursor-not-allowed"
                  : "border-muted-foreground/40 text-secondary-foreground hover:border-olive hover:text-foreground"}`}>
              {t}
            </button>
          );
        })}
      </div>
      <button onClick={save} disabled={busy} data-testid="specialty-save"
        className="mt-4 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-5 py-2.5 transition-colors">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save specialties"} <span className="font-mono text-[10px] opacity-80">({tags.length}/3)</span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- Contact & Address
function ContactPanel() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState("");
  const [reqOpen, setReqOpen] = useState(false);
  const [reqEmail, setReqEmail] = useState("");
  const [reqReason, setReqReason] = useState("");

  const load = useCallback(() => {
    api.get("/me/contact-changes").then(({ data }) => {
      setData(data);
      setEmail(data.email || "");
      setPhone(data.phone || "");
    }).catch(() => {});
    api.get("/auth/me").then(({ data }) => setZip(data.zip_code || "")).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="font-mono text-xs text-muted-foreground uppercase">Loading…</p>;

  const locked = data.contact_locked;
  const pending = (data.requests || []).find((r) => r.field === "email" && (r.status === "pending" || r.status === "approved"));

  const saveEmail = async () => {
    setBusy("email");
    try {
      await api.put("/me/contact", { email: email.trim().toLowerCase() });
      toast.success("Verification email sent — confirm it to finish.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const savePhone = async () => {
    setBusy("phone");
    try {
      await api.put("/me/contact", { phone: phone.trim() });
      toast.success("Phone number saved.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const saveZip = async () => {
    setBusy("zip");
    try {
      const { data: r } = await api.put("/me/contact", { zip_code: zip.trim() });
      if (r.status === "noop") toast.message("No change.");
      else toast.success("Location updated.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submitRequest = async () => {
    if (reqReason.trim().length < 10) { toast.error("Add a reason of at least 10 characters."); return; }
    setBusy("request");
    try {
      await api.post("/me/contact-change", { field: "email", new_value: reqEmail.trim().toLowerCase(), reason: reqReason.trim() });
      toast.success("Request submitted. We emailed both your current and new addresses.");
      setReqOpen(false); setReqEmail(""); setReqReason("");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const cancelRequest = async () => {
    if (!pending) return;
    setBusy("cancel");
    try {
      await api.post(`/me/contact-change/${pending.request_id}/cancel`);
      toast.success("Request cancelled.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const emailUnchanged = email.trim().toLowerCase() === (data.email || "").toLowerCase();

  return (
    <div data-testid="contact-panel" className="space-y-6">
      {/* EMAIL */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Mail className="w-4 h-4 text-olive-bright" />
          <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">Email address</label>
          {data.email_verified
            ? <span className="font-mono text-[10px] uppercase text-olive-bright inline-flex items-center gap-1" data-testid="contact-email-verified"><CheckCircle2 className="w-3 h-3" /> verified</span>
            : <span className="font-mono text-[10px] uppercase text-amber-400 inline-flex items-center gap-1" data-testid="contact-email-unverified"><AlertTriangle className="w-3 h-3" /> unverified</span>}
          {locked && <span className="font-mono text-[10px] uppercase text-muted-foreground inline-flex items-center gap-1" data-testid="contact-email-locked"><Lock className="w-3 h-3" /> locked</span>}
        </div>

        {locked ? (
          <div>
            <div className="bg-input border border-sniper px-3 py-2.5 font-mono text-sm text-foreground break-all">{data.email}</div>
            <p className="font-body text-[12px] text-secondary-foreground mt-1.5">
              Your email is locked because your identity is verified. Changing it needs a reason, admin approval, and confirmation from the new address — this protects you from account takeover.
            </p>

            {pending ? (
              <div className="mt-3 border border-amber-400/40 bg-amber-400/5 p-3" data-testid="contact-pending-request">
                <p className="font-mono text-[11px] uppercase text-amber-400 flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5" /> {pending.status === "approved" ? "Approved — confirm from your new inbox" : "Change requested — awaiting admin review"}
                </p>
                <p className="font-mono text-xs text-foreground mt-1 break-all">→ {pending.requested_value}</p>
                <button onClick={cancelRequest} disabled={busy === "cancel"} data-testid="contact-cancel-request"
                  className="mt-2 inline-flex items-center gap-1 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                  <X className="w-3 h-3" /> Cancel request
                </button>
              </div>
            ) : reqOpen ? (
              <div className="mt-3 border border-sniper bg-input/50 p-3" data-testid="contact-request-form">
                <label className="font-mono text-[10px] uppercase text-secondary-foreground block mb-1">New email address</label>
                <input value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} data-testid="contact-request-new-email"
                  className={inputCls} placeholder="new@email.com" autoCapitalize="none" spellCheck={false} />
                <label className="font-mono text-[10px] uppercase text-secondary-foreground block mb-1 mt-3">Reason (min 10 chars)</label>
                <textarea value={reqReason} onChange={(e) => setReqReason(e.target.value)} rows={2} maxLength={500}
                  data-testid="contact-request-reason" className={inputCls}
                  placeholder="e.g. I no longer have access to my old inbox." />
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={submitRequest} disabled={busy === "request" || !reqEmail.trim() || reqReason.trim().length < 10}
                    data-testid="contact-request-submit"
                    className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
                    <Save className="w-3.5 h-3.5" /> {busy === "request" ? "Submitting…" : "Submit request"}
                  </button>
                  <button onClick={() => { setReqOpen(false); setReqEmail(""); setReqReason(""); }}
                    className="font-mono uppercase text-[10px] text-muted-foreground hover:text-foreground px-2 py-2">Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setReqOpen(true)} data-testid="contact-request-change-btn"
                className="mt-3 inline-flex items-center gap-2 border border-olive/60 text-olive-bright hover:bg-olive/10 font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
                <KeyRound className="w-3.5 h-3.5" /> Request email change
              </button>
            )}
          </div>
        ) : (
          <div>
            <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email-input"
              className={inputCls} autoCapitalize="none" spellCheck={false} placeholder="you@email.com" />
            <button onClick={saveEmail} disabled={busy === "email" || emailUnchanged || !email.trim()} data-testid="contact-email-save"
              className="mt-2.5 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
              <Save className="w-3.5 h-3.5" /> {busy === "email" ? "Saving…" : "Save email"}
            </button>
            <p className="font-body text-[12px] text-secondary-foreground mt-1.5">
              We'll send a verification link to your new address. It becomes locked once you verify your identity.
            </p>
          </div>
        )}
      </div>

      <Crosshair className="my-1 opacity-40" />

      {/* PHONE */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Phone className="w-4 h-4 text-olive-bright" />
          <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">Phone number</label>
          <span className="font-mono text-[10px] uppercase text-muted-foreground" data-testid="contact-phone-unverified">not verified</span>
        </div>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="contact-phone-input"
          className={inputCls} placeholder="(555) 555-0123" inputMode="tel" />
        <button onClick={savePhone} disabled={busy === "phone" || phone.trim() === (data.phone || "")} data-testid="contact-phone-save"
          className="mt-2.5 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
          <Save className="w-3.5 h-3.5" /> {busy === "phone" ? "Saving…" : "Save phone"}
        </button>
        <p className="font-body text-[12px] text-secondary-foreground mt-1.5">
          Optional. Phone verification isn't available yet, so this is never shown as verified.
        </p>
      </div>

      <Crosshair className="my-1 opacity-40" />

      {/* ZIP */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <MapPin className="w-4 h-4 text-olive-bright" />
          <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">ZIP code</label>
        </div>
        <input value={zip} onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
          data-testid="contact-zip-input" className={`${inputCls} max-w-[160px]`} placeholder="90210" inputMode="numeric" />
        <button onClick={saveZip} disabled={busy === "zip" || zip.length !== 5} data-testid="contact-zip-save"
          className="mt-2.5 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
          <Save className="w-3.5 h-3.5" /> {busy === "zip" ? "Saving…" : "Save ZIP"}
        </button>
        <p className="font-body text-[12px] text-secondary-foreground mt-1.5">
          Your ZIP stays private — only the derived city &amp; state show on your public profile.
        </p>
      </div>
    </div>
  );
}

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const daysInMonth = (m) => [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][(Number(m) || 1) - 1] || 31;

// ---------------------------------------------------------------- Birthday & offers
export function BirthdayPanel({ me, onChanged }) {
  const [month, setMonth] = useState(me?.birth_month ? String(me.birth_month) : "");
  const [day, setDay] = useState(me?.birth_day ? String(me.birth_day) : "");
  const [offers, setOffers] = useState(!!me?.notification_prefs?.birthday_offers);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMonth(me?.birth_month ? String(me.birth_month) : "");
    setDay(me?.birth_day ? String(me.birth_day) : "");
    setOffers(!!me?.notification_prefs?.birthday_offers);
  }, [me]);

  const bumps = (me?.rewards || []).filter((r) => r.kind === "free_bump");
  const bothOrNeither = (!!month && !!day) || (!month && !day);
  const savedM = me?.birth_month ? String(me.birth_month) : "";
  const savedD = me?.birth_day ? String(me.birth_day) : "";
  const dirty = month !== savedM || day !== savedD || offers !== !!me?.notification_prefs?.birthday_offers;
  const maxDay = daysInMonth(month);
  const dayVal = day && Number(day) > maxDay ? "" : day;

  const save = async () => {
    if (!bothOrNeither) { toast.error("Pick both a month and a day, or clear both."); return; }
    setBusy(true);
    try {
      await api.put("/me/birthday", {
        birth_month: month ? Number(month) : null,
        birth_day: dayVal ? Number(dayVal) : null,
      });
      await api.put("/me/notifications", { birthday_offers: offers });
      toast.success("Birthday preferences saved.");
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.put("/me/birthday", { birth_month: null, birth_day: null });
      toast.success("Birthday cleared.");
      setMonth(""); setDay("");
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="birthday-panel" className="space-y-4">
      <p className="font-body text-[12px] text-secondary-foreground -mt-1">
        Optional. We'll send you something on your birthday. Month and day only — we never ask for the year.
      </p>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wide text-secondary-foreground mb-1">Month</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="birthday-month"
            className={`${inputCls} w-[160px]`}>
            <option value="">—</option>
            {MONTHS.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wide text-secondary-foreground mb-1">Day</label>
          <select value={dayVal} onChange={(e) => setDay(e.target.value)} data-testid="birthday-day" disabled={!month}
            className={`${inputCls} w-[100px] disabled:opacity-40`}>
            <option value="">—</option>
            {Array.from({ length: maxDay }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 border border-sniper bg-input/40 px-3 py-3" data-testid="birthday-offers-row">
        <div className="min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-wide text-foreground">Birthday offers</p>
          <p className="font-body text-[12px] text-secondary-foreground mt-0.5">
            Off by default. Turn this on to get a birthday perk by email. You can turn it off anytime.
          </p>
        </div>
        <Switch checked={offers} onCheckedChange={(v) => setOffers(!!v)} data-testid="birthday-offers-toggle" />
      </div>

      {bumps.length > 0 && (
        <div className="border border-[#6B551C] bg-[#1a1608] px-3 py-2.5 flex items-center gap-2.5" data-testid="birthday-reward-active">
          <Cake className="w-4 h-4" style={{ color: "#E8C46A" }} />
          <p className="font-mono text-[11px] text-[#E8C46A]">
            You have {bumps.length} free listing bump{bumps.length > 1 ? "s" : ""}
            {bumps[0].expires_at ? ` — use by ${new Date(bumps[0].expires_at).toLocaleDateString()}` : ""}.
          </p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy || !dirty} data-testid="birthday-save"
          className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
          <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
        </button>
        {(savedM || savedD) && (
          <button onClick={clear} disabled={busy} data-testid="birthday-clear"
            className="font-mono uppercase text-[10px] text-muted-foreground hover:text-foreground px-2 py-2">Clear birthday</button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Notifications
const NOTIF_CATS = [
  { key: "marketplace", label: "Marketplace Messages", desc: "Buyers & sellers messaging you about a listing." },
  { key: "staff", label: "Staff Messages", desc: "Direct messages from the OpticSwap team." },
  { key: "group", label: "Group Messages", desc: "Messages in group chats you belong to." },
];

export function NotificationsPanel({ me, onChanged }) {
  const seed = () => {
    const r = me?.notification_prefs || {};
    const m = {};
    for (const c of NOTIF_CATS) {
      const s = (r.messages || {})[c.key] || {};
      m[c.key] = { inapp: s.inapp !== false, email: s.email === true };
    }
    return { master: r.email_master !== false, freq: r.email_frequency === "digest" ? "digest" : "instant", msgs: m,
             forum: { mention_email: (r.forum?.mention_email) !== false, reply_email: (r.forum?.reply_email) !== false } };
  };
  const [master, setMaster] = useState(() => seed().master);
  const [freq, setFreq] = useState(() => seed().freq);
  const [msgs, setMsgs] = useState(() => seed().msgs);
  const [forum, setForum] = useState(() => seed().forum);
  const [busy, setBusy] = useState(false);
  // Re-seed once the resolved prefs arrive from /auth/me (first paint may use the lean auth user).
  const prefsKey = JSON.stringify(me?.notification_prefs?.messages || {}) + String(me?.notification_prefs?.email_master) + String(me?.notification_prefs?.email_frequency) + JSON.stringify(me?.notification_prefs?.forum || {});
  useEffect(() => {
    const s = seed();
    setMaster(s.master);
    setFreq(s.freq);
    setMsgs(s.msgs);
    setForum(s.forum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsKey]);

  const setCat = (key, field, val) => setMsgs((p) => ({ ...p, [key]: { ...p[key], [field]: val } }));
  const setForumPref = (key, val) => setForum((p) => ({ ...p, [key]: val }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/notifications", { email_master: master, email_frequency: freq, messages: msgs, forum });
      toast.success("Notification preferences saved.");
      onChanged?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't save preferences.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="notifications-panel" className="space-y-4">
      {/* Master email switch */}
      <div className="flex items-start justify-between gap-4 border border-sniper bg-input/40 px-3 py-3" data-testid="notif-email-master-row">
        <div className="min-w-0">
          <p className="font-heading text-sm uppercase tracking-tight text-foreground">Email Notifications</p>
          <p className="text-xs text-secondary-foreground mt-0.5">Master switch for all email alerts. When off, no emails are sent regardless of the per-type settings below.</p>
        </div>
        <Switch checked={master} onCheckedChange={(v) => setMaster(!!v)} data-testid="notif-email-master-toggle" />
      </div>

      {/* Email frequency — Instant vs Daily digest */}
      <div className={`border border-sniper px-3 py-3 ${master ? "" : "opacity-50"}`} data-testid="notif-email-frequency-row">
        <p className="font-heading text-sm uppercase tracking-tight text-foreground">Email frequency</p>
        <p className="text-xs text-secondary-foreground mt-0.5 mb-2.5">How your missed-message emails arrive. In-app alerts are unaffected.</p>
        <div className="inline-flex border border-sniper overflow-hidden" role="group">
          {[
            { id: "instant", label: "Instant", desc: "One email per missed message" },
            { id: "digest", label: "Daily digest", desc: "One batched email each morning" },
          ].map((opt) => (
            <button key={opt.id} type="button" disabled={!master} onClick={() => setFreq(opt.id)}
              data-testid={`notif-frequency-${opt.id}`} data-active={freq === opt.id || undefined}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-wide transition-colors disabled:cursor-not-allowed ${freq === opt.id ? "bg-olive text-white" : "bg-input text-muted-foreground hover:text-foreground"}`}>
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2" data-testid="notif-frequency-desc">
          {freq === "digest"
            ? "You'll get one email each morning summarizing everything you missed — grouped by type. Message types with email turned off are never included."
            : "You get an email as soon as a missed message arrives (throttled so a burst doesn't spam you)."}
        </p>
      </div>

      {/* Per-category rows */}
      <div className="border border-sniper">
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-3 py-2 border-b border-sniper bg-obsidian/40">
          <span className="font-mono text-[10px] uppercase tracking-[1.4px] text-muted-foreground">Message type</span>
          <span className="font-mono text-[10px] uppercase tracking-[1.4px] text-muted-foreground text-center w-14">In-App</span>
          <span className="font-mono text-[10px] uppercase tracking-[1.4px] text-muted-foreground text-center w-14">Email</span>
        </div>
        {NOTIF_CATS.map((c) => (
          <div key={c.key} className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center px-3 py-3 border-b border-sniper/60 last:border-b-0" data-testid={`notif-row-${c.key}`}>
            <div className="min-w-0">
              <p className="font-heading text-sm uppercase tracking-tight text-foreground">{c.label}</p>
              <p className="text-xs text-secondary-foreground mt-0.5">{c.desc}</p>
            </div>
            <div className="w-14 flex justify-center">
              <Switch checked={msgs[c.key].inapp} onCheckedChange={(v) => setCat(c.key, "inapp", !!v)} data-testid={`notif-${c.key}-inapp-toggle`} />
            </div>
            <div className="w-14 flex justify-center">
              <Switch checked={master && msgs[c.key].email} disabled={!master} onCheckedChange={(v) => setCat(c.key, "email", !!v)} data-testid={`notif-${c.key}-email-toggle`} />
            </div>
          </div>
        ))}
      </div>
      {!master && (
        <p className="font-mono text-[11px] text-amber-400" data-testid="notif-email-off-hint">Email is off site-wide — turn on the master switch to enable per-type email alerts.</p>
      )}

      {/* Forum email alerts — in-app is always on (Inbox → Forum); these govern the extra email. */}
      <div className="border border-sniper" data-testid="notif-forum-section">
        <div className="grid grid-cols-[1fr_auto] gap-x-4 px-3 py-2 border-b border-sniper bg-obsidian/40">
          <span className="font-mono text-[10px] uppercase tracking-[1.4px] text-muted-foreground">Forum — Email me too</span>
          <span className="font-mono text-[10px] uppercase tracking-[1.4px] text-muted-foreground text-center w-14">Email</span>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-x-4 items-center px-3 py-3 border-b border-sniper/60" data-testid="notif-forum-mention-row">
          <div className="min-w-0">
            <p className="font-heading text-sm uppercase tracking-tight text-foreground">Mentions</p>
            <p className="text-xs text-secondary-foreground mt-0.5">When someone tags you with @username in a thread or reply. Always shows in your inbox.</p>
          </div>
          <div className="w-14 flex justify-center">
            <Switch checked={master && forum.mention_email} disabled={!master} onCheckedChange={(v) => setForumPref("mention_email", !!v)} data-testid="notif-forum-mention-email-toggle" />
          </div>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-x-4 items-center px-3 py-3" data-testid="notif-forum-reply-row">
          <div className="min-w-0">
            <p className="font-heading text-sm uppercase tracking-tight text-foreground">Watched thread replies</p>
            <p className="text-xs text-secondary-foreground mt-0.5">New replies on threads you're watching. Always shows in your inbox.</p>
          </div>
          <div className="w-14 flex justify-center">
            <Switch checked={master && forum.reply_email} disabled={!master} onCheckedChange={(v) => setForumPref("reply_email", !!v)} data-testid="notif-forum-reply-email-toggle" />
          </div>
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="notif-prefs-save"
        className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-5 py-2.5 transition-colors">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save preferences"}
      </button>
    </div>
  );
}


// ---------------------------------------------------------------- Social handles
const SOCIAL_FIELDS = [
  { key: "instagram", label: "Instagram", prefix: "@", placeholder: "yourhandle" },
  { key: "youtube", label: "YouTube", prefix: "@", placeholder: "channel" },
  { key: "x", label: "X (Twitter)", prefix: "@", placeholder: "yourhandle" },
  { key: "tiktok", label: "TikTok", prefix: "@", placeholder: "yourhandle" },
  { key: "facebook", label: "Facebook", prefix: "", placeholder: "facebook.com/you or your page" },
  { key: "website", label: "Website", prefix: "", placeholder: "https://…" },
];

export function SocialHandlesEditor({ initial, onChanged }) {
  const base = () => SOCIAL_FIELDS.reduce((a, f) => ({ ...a, [f.key]: (initial || {})[f.key] || "" }), {});
  const [vals, setVals] = useState(base);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setVals((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/me/socials", vals);
      setVals(SOCIAL_FIELDS.reduce((a, f) => ({ ...a, [f.key]: (data.socials || {})[f.key] || "" }), {}));
      toast.success("Social handles saved.");
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="socials-editor" className="space-y-3">
      <p className="text-xs font-body text-secondary-foreground -mt-1">
        Add your handles so we can credit you when we feature your gear. Tag <span className="text-foreground">#OpticSwap</span> on Instagram to get on the In The Wild wall.
      </p>
      {SOCIAL_FIELDS.map((f) => (
        <div key={f.key}>
          <label className="block font-mono text-[10px] uppercase tracking-[1.4px] text-muted-foreground mb-1">{f.label}</label>
          <div className="flex items-center border border-sniper bg-input focus-within:border-olive transition-colors">
            {f.prefix && <span className="pl-3 text-muted-foreground font-mono text-sm select-none">{f.prefix}</span>}
            <input value={vals[f.key]} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder}
              data-testid={`socials-${f.key}-input`}
              className="flex-1 bg-transparent outline-none px-3 py-2.5 text-foreground font-mono text-sm" />
          </div>
        </div>
      ))}
      <button onClick={save} disabled={busy} data-testid="socials-save-btn"
        className="mt-1 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-5 py-2.5 transition-colors">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save handles"}
      </button>
    </div>
  );
}

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/"); };
  const [me, setMe] = useState(null);
  const reloadMe = useCallback(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    refresh();
  }, [refresh]);
  useEffect(() => { reloadMe(); }, [reloadMe]);

  // Deep-link from "In The Wild" (/settings/profile#socials) scrolls to the handles section.
  useEffect(() => {
    if (me && window.location.hash === "#socials") {
      const el = document.getElementById("socials");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [me]);

  if (!user) return null;
  const data = me || user;
  const licenseBadges = (data.earned_badges || []).filter((b) => b.id === "ffl" || b.id === "sot");
  // Best-effort prefill: existing accounts may have a legacy free-form `name` but no first/last.
  const _nameParts = (data.name || "").trim().split(/\s+/).filter(Boolean);
  const seedFirst = data.first_name || (!data.first_name && !data.last_name ? (_nameParts[0] || "") : "");
  const seedLast = data.last_name || (!data.first_name && !data.last_name ? _nameParts.slice(1).join(" ") : "");

  return (
    <div className="min-h-screen text-foreground" data-testid="settings-profile-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </Link>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Profile &amp; Badges</h1>
        <p className="text-sm text-secondary-foreground mt-1 font-body">Your public identity on OpticSwap — handle, verification, badges and specialties.</p>

        <Section icon={AtSign} title="Username" testId="settings-username-section">
          <UsernameEditor current={data.username} onChanged={reloadMe} />
        </Section>

        <Section icon={Pencil} title="About You" testId="settings-about-section"
          subtitle="Your real name (private by default) and a short public bio. Buyers read the bio on your profile.">
          <AboutYouEditor
            handle={data.username}
            initialFirst={seedFirst}
            initialLast={seedLast}
            initialShowName={data.show_name_public}
            initialBio={data.bio}
            onChanged={reloadMe} />
        </Section>

        <div id="socials">
          <Section icon={Share2} title="Social Handles" testId="settings-socials-section"
            subtitle="Your public social links — shown on your profile and used to credit your #OpticSwap posts on the In The Wild wall.">
            <SocialHandlesEditor initial={data.socials} onChanged={reloadMe} />
          </Section>
        </div>

        <Section icon={Mail} title="Contact & Address" testId="settings-contact-section"
          subtitle="Your email, phone and location. A verified identity locks your email against takeover.">
          <ContactPanel />
        </Section>

        <Section icon={Cake} title="Birthday" testId="settings-birthday-section"
          subtitle="Optional, for birthday perks. Month and day only — never the year.">
          <BirthdayPanel me={data} onChanged={reloadMe} />
        </Section>

        <Section icon={Bell} title="Notifications" testId="settings-notifications-section"
          subtitle="Control which new messages alert you in-app and by email. Changes only affect your account.">
          <NotificationsPanel me={data} onChanged={reloadMe} />
        </Section>

        <Section icon={ShieldCheck} title="Verified ID" testId="settings-identity-section"
          subtitle="Government-ID verification via Stripe Identity.">
          <IdentityPanel />
        </Section>

        <Section icon={Award} title="Badges" testId="settings-badges-section"
          subtitle="Earned automatically from real activity — never self-declared.">
          <BadgesPanel />
        </Section>

        {licenseBadges.length > 0 && (
          <Section icon={FileCheck} title="Licenses" testId="settings-licenses-section"
            subtitle="FFL / SOT are verified by our team. Contact support to update them.">
            <BadgeRow badges={licenseBadges} accountTone={data.account_tone || "green"} />
          </Section>
        )}

        <Section icon={Target} title="Specialties" testId="settings-specialty-section">
          <SpecialtyPicker initial={data.specialty_tags} onChanged={reloadMe} />
        </Section>

        {/* Log out — separated block at the very bottom, full-width so it's reachable on a phone
            without any horizontal scroll (this page was reported unusable on mobile for sign-out). */}
        <div className="mt-8 pt-6 border-t border-sniper" data-testid="settings-logout-section">
          <button onClick={doLogout} data-testid="settings-logout-btn"
            className="w-full flex items-center justify-center gap-2 border border-blaze/50 text-blaze hover:bg-blaze/10 font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
            <LogOut className="w-4 h-4" /> Log out
          </button>
          <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground/70 text-center mt-2">Signs you out on this device and ends this session.</p>
        </div>
      </div>
    </div>
  );
}
